/**
 * Shipping Protection Custom Element
 * Handles automatic adding/removing of shipping protection variant and UI synchronization.
 */

if (!customElements.get('shipping-protection')) {
  // Global serialization lock to prevent concurrent race conditions across instances
  window._spLock = window._spLock || Promise.resolve();
  const runWithLock = (fn) => {
    const next = window._spLock.then(fn, fn);
    window._spLock = next;
    return next;
  };

  customElements.define(
    'shipping-protection',
    class ShippingProtection extends HTMLElement {
      constructor() {
        super();
        this.variantId = this.getAttribute('data-variant-id');
        this.checkbox = this.querySelector('input[type="checkbox"]');
      }

      connectedCallback() {
        if (!this.checkbox || !this.variantId) return;

        // Schedule initial sync with a short delay to merge with concurrent cart updates (e.g., free gift unlocks)
        this.scheduleInit(150);

        // Listen for user checkbox changes
        this.checkboxListener = () => {
          // Clear any pending automatic init when user explicitly toggles
          clearTimeout(this.initTimeout);
          runWithLock(() => this.onCheckboxChange());
        };
        this.checkbox.addEventListener('change', this.checkboxListener);

        // Listen for cart updates from other sources
        this.cartUpdateUnsubscriber = theme.pubsub.subscribe(
          theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
          this.onCartUpdate.bind(this)
        );
      }

      disconnectedCallback() {
        clearTimeout(this.initTimeout);
        clearTimeout(this.updateTimeout);
        if (this.checkboxListener && this.checkbox) {
          this.checkbox.removeEventListener('change', this.checkboxListener);
        }
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      scheduleInit(delay = 350) {
        clearTimeout(this.initTimeout);
        this.initTimeout = setTimeout(() => {
          runWithLock(() => this.init());
        }, delay);
      }

      async init() {
        try {
          const cart = await this.getCart();
          const variantIdNum = Number(this.variantId);
          const existingItem = cart.items.find((item) => item.variant_id === variantIdNum);
          const hasOtherItems = cart.items.some((item) => item.variant_id !== variantIdNum);
          const isOptedOut = localStorage.getItem('shipping_protection_opt_out') === 'true';

          // Case 0: Cart is empty or only has shipping protection -> Remove it
          if (cart.item_count === 0 || !hasOtherItems) {
            if (existingItem) {
              await this.updateCartQuantity(existingItem.key, 0);
            }
            if (this.checkbox) this.checkbox.checked = false;
            return;
          }

          // Case 1: Auto Add (if not in cart, user hasn't opted out, and cart has other items)
          if (!existingItem && !isOptedOut && hasOtherItems) {
            if (this.checkbox) this.checkbox.checked = true;
            await this.addToCart(variantIdNum, 1);
            return;
          }

          // Case 2: Auto Remove (if in cart but opt-out is true)
          if (existingItem && isOptedOut) {
            if (this.checkbox) this.checkbox.checked = false;
            await this.updateCartQuantity(existingItem.key, 0);
            return;
          }

          // Case 3: Ensure quantity is always 1 (if in cart)
          if (existingItem && existingItem.quantity !== 1) {
            await this.updateCartQuantity(existingItem.key, 1);
            return;
          }

          // Case 4: Sync Checkbox UI
          if (this.checkbox) {
            this.checkbox.checked = !!existingItem && !isOptedOut;
          }
        } catch (error) {
          console.error('Shipping Protection Init Error:', error);
        }
      }

      async onCheckboxChange() {
        try {
          const variantIdNum = Number(this.variantId);
          if (this.checkbox.checked) {
            localStorage.removeItem('shipping_protection_opt_out');
            const cart = await this.getCart();
            const item = cart.items.find((it) => it.variant_id === variantIdNum);
            if (item) {
              if (item.quantity !== 1) {
                await this.updateCartQuantity(item.key, 1);
              }
            } else {
              await this.addToCart(variantIdNum, 1);
            }
          } else {
            localStorage.setItem('shipping_protection_opt_out', 'true');
            const cart = await this.getCart();
            const item = cart.items.find((it) => it.variant_id === variantIdNum);
            if (item) {
              await this.updateCartQuantity(item.key, 0);
            }
          }
        } catch (error) {
          console.error('Shipping Protection Toggle Error:', error);
        }
      }

      onCartUpdate(event) {
        // Prevent infinite loops if the update came from here
        if (event.source === 'shipping-protection') return;

        // Schedule init logic with delay to allow free gift and other cart updates to finish first
        this.scheduleInit(350);
      }

      async getCart() {
        const res = await fetch(theme.routes.cart_url + '.js');
        return await res.json();
      }

      async addToCart(id, quantity) {
        // First check current cart state to prevent double adding
        const currentCart = await this.getCart();
        const existingItem = currentCart.items.find((item) => item.variant_id === id);

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle }
          })
        );

        if (existingItem) {
          // If already in cart, enforce quantity = 1 via change.js
          if (existingItem.quantity !== quantity) {
            await this.updateCartQuantity(existingItem.key, quantity);
          }
          return;
        }

        const res = await fetch(theme.routes.cart_add_url + '.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: [{ id, quantity }],
            sections: sectionsToBundle
          })
        });
        const parsedState = await res.json();

        // Shopify's /cart/add.js doesn't return the full cart, so we fetch it
        const cartRes = await fetch(theme.routes.cart_url + '.js');
        const cartJson = await cartRes.json();
        cartJson.sections = parsedState.sections;

        // Publish update to theme
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'shipping-protection',
          cart: cartJson
        });
      }

      async updateCartQuantity(key, quantity) {
        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle }
          })
        );

        const res = await fetch(theme.routes.cart_change_url + '.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: key,
            quantity,
            sections: sectionsToBundle
          })
        });
        const parsedState = await res.json();

        // Publish update to theme
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'shipping-protection',
          cart: parsedState
        });
      }
    }
  );
}
